import React, { useState, useEffect, useCallback, useRef } from "react";
import moment from "moment";
import { Form, Select, Input, Button, Icon } from "semantic-ui-react";

const MONTH_OPTIONS = moment.months().map((name, index) => ({
    key: index + 1,
    text: name,
    value: index + 1
}));

const MIN_YEAR = 2000;

const MonthYearJumper = ({ currentDate, navigateToDate }) => {
    const baseDate = currentDate ? moment(currentDate) : moment();

    const [isOpen, setIsOpen] = useState(false);
    const [selectedMonth, setSelectedMonth] = useState(baseDate.month() + 1);
    const [selectedYear, setSelectedYear] = useState(baseDate.year());

    const containerRef = useRef(null);

    useEffect(() => {
        const date = currentDate ? moment(currentDate) : moment();
        setSelectedMonth(date.month() + 1);
        setSelectedYear(date.year());
    }, [currentDate]);

    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleToggleOpen = useCallback(() => {
        setIsOpen(prev => !prev);
    }, []);

    const handleMonthChange = useCallback((e, { value }) => {
        setSelectedMonth(value);
    }, []);

    const handleYearChange = useCallback((e, { value }) => {
        if (value === '' || /^\d{0,4}$/.test(value)) {
            setSelectedYear(value === '' ? '' : Number(value));
        }
    }, []);

    const isFutureSelection = useCallback(() => {
        if (!selectedYear || !selectedMonth) return true;
        const selected = moment(`${selectedYear}-${selectedMonth}-01`, 'YYYY-M-DD');
        return selected.isAfter(moment(), 'month');
    }, [selectedMonth, selectedYear]);

    const handleGoClick = useCallback(() => {
        if (isFutureSelection()) return;
        navigateToDate(moment(`${selectedYear}-${selectedMonth}-01`, 'YYYY-M-DD').toDate());
        setIsOpen(false);
    }, [selectedMonth, selectedYear, isFutureSelection, navigateToDate]);

    const handleKeyPress = useCallback((e) => {
        if (e.key === 'Enter') {
            handleGoClick();
        }
    }, [handleGoClick]);

    const currentYear = moment().year();
    const disableGo = isFutureSelection();

    return (
        <div ref={containerRef} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <Button
                type='button'
                icon
                basic
                size='small'
                active={isOpen}
                onClick={handleToggleOpen}
                title='Jump to month/year'
            >
                <Icon name='calendar alternate outline' />
            </Button>
            {isOpen && (
                <Form onKeyPress={handleKeyPress} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: '8px' }}>
                    <Form.Group style={{ marginBottom: 0, alignItems: 'center' }}>
                        <Form.Field>
                            <Select
                                compact
                                size='small'
                                value={selectedMonth}
                                onChange={handleMonthChange}
                                options={MONTH_OPTIONS}
                                placeholder='Month'
                            />
                        </Form.Field>
                        <Form.Field>
                            <Input
                                type='number'
                                size='small'
                                min={MIN_YEAR}
                                max={currentYear}
                                value={selectedYear}
                                onChange={handleYearChange}
                                placeholder='Year'
                                style={{ width: '90px' }}
                            />
                        </Form.Field>
                        <Form.Field>
                            <Button
                                type='button'
                                primary
                                size='small'
                                disabled={disableGo}
                                onClick={handleGoClick}
                            >
                                Go
                            </Button>
                        </Form.Field>
                    </Form.Group>
                </Form>
            )}
        </div>
    );
};

export default MonthYearJumper;
